/**
 * AnnouncementManager.jsx — Redesigned · Dark Luxury Fintech
 * Accent: blue/indigo (Broadcast identity)
 *
 * Améliorations UX vs original :
 * - window.confirm() → ConfirmDialog natif
 * - Modal form → Drawer latéral spring-animated
 * - Live color preview dans le drawer avec picker hex natif
 * - Priority badge visuel avec rang coloré
 * - Toggle "Display All" animé dans le header
 * - Reorder visuel (priority indicator)
 * - Stats MiniStat animées
 * - Filtre active/inactive
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { collection, getDocs, doc, setDoc, deleteDoc, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Megaphone, Plus, Edit2, Trash2, Bell,
  RefreshCw, X, Check, Link, AlertTriangle,
  ChevronDown, Filter, ToggleLeft, ToggleRight,
  ArrowUp, ArrowDown, Eye
} from 'lucide-react';
import { toast } from 'react-toastify';

// ─── Atoms ────────────────────────────────────────────────────────────────
const GlassPanel = ({ children, style = {} }) => (
  <div style={{ background: 'rgba(17,24,39,.85)', border: '1px solid rgba(255,255,255,.06)', borderRadius: 16, backdropFilter: 'blur(16px)', overflow: 'hidden', ...style }}>
    {children}
  </div>
);

const MiniStat = ({ label, value, accent = '#3b82f6', icon: Icon, index = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
    transition={{ delay: index * .07, duration: .45, ease: [.22, 1, .36, 1] }}
    whileHover={{ y: -3, transition: { duration: .15 } }}
    style={{ background: 'rgba(17,24,39,.85)', border: `1px solid ${accent}33`, borderRadius: 14, padding: '14px 16px', boxShadow: `0 0 24px ${accent}0d`, position: 'relative', overflow: 'hidden' }}
  >
    <div style={{ position: 'absolute', top: -24, right: -24, width: 72, height: 72, borderRadius: '50%', background: `${accent}18`, filter: 'blur(20px)', pointerEvents: 'none' }} />
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', position: 'relative' }}>
      <div>
        <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.14em', color: '#475569', marginBottom: 8 }}>{label}</p>
        <p style={{ fontSize: 22, fontWeight: 700, color: '#fff', lineHeight: 1 }}>{value}</p>
      </div>
      {Icon && (
        <div style={{ width: 32, height: 32, borderRadius: 9, background: `${accent}18`, border: `1px solid ${accent}33`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon size={15} color={accent} strokeWidth={1.8} />
        </div>
      )}
    </div>
    <motion.div initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: .5, delay: .2 + index * .07 }}
      style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: 2, background: accent, opacity: .5, transformOrigin: 'left' }} />
  </motion.div>
);

const Btn = ({ children, onClick, variant = 'ghost', size = 'sm', disabled = false, style = {}, type = 'button' }) => {
  const V = {
    ghost:  { background: 'rgba(255,255,255,.06)', color: '#cbd5e1', border: '1px solid rgba(255,255,255,.08)' },
    blue:   { background: 'rgba(59,130,246,.15)',  color: '#60a5fa', border: '1px solid rgba(59,130,246,.3)'  },
    indigo: { background: 'rgba(99,102,241,.15)',  color: '#818cf8', border: '1px solid rgba(99,102,241,.3)'  },
    green:  { background: 'rgba(16,185,129,.15)',  color: '#34d399', border: '1px solid rgba(16,185,129,.3)'  },
    red:    { background: 'rgba(239,68,68,.15)',   color: '#f87171', border: '1px solid rgba(239,68,68,.3)'   },
    amber:  { background: 'rgba(245,158,11,.15)',  color: '#f59e0b', border: '1px solid rgba(245,158,11,.3)'  },
  };
  const v = V[variant] || V.ghost;
  const pad = size === 'xs' ? '3px 8px' : size === 'sm' ? '5px 12px' : '8px 18px';
  return (
    <button type={type} onClick={onClick} disabled={disabled} style={{ ...v, padding: pad, borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? .45 : 1, display: 'inline-flex', alignItems: 'center', gap: 5, transition: 'opacity .15s', whiteSpace: 'nowrap', ...style }}>
      {children}
    </button>
  );
};

const Toggle = ({ value, onChange, accent = '#3b82f6' }) => (
  <div onClick={onChange}
    style={{ width: 36, height: 20, borderRadius: 10, background: value ? accent : 'rgba(255,255,255,.1)', position: 'relative', transition: 'background .2s', flexShrink: 0, cursor: 'pointer' }}>
    <motion.div animate={{ x: value ? 18 : 2 }} transition={{ type: 'spring', stiffness: 500, damping: 30 }}
      style={{ position: 'absolute', top: 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,.3)' }} />
  </div>
);

const FieldLabel = ({ children }) => (
  <label style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.12em', color: '#475569', display: 'block', marginBottom: 5 }}>{children}</label>
);

const DI = ({ value, onChange, placeholder, type = 'text', style = {} }) => (
  <input type={type} value={value ?? ''} onChange={onChange} placeholder={placeholder}
    style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 9, padding: '8px 11px', color: '#e2e8f0', fontSize: 12, width: '100%', outline: 'none', ...style }} />
);

// ─── Priority badge ───────────────────────────────────────────────────────
const PriorityBadge = ({ priority }) => {
  const p = Number(priority) || 0;
  const [bg, color, label] =
    p >= 10 ? ['rgba(239,68,68,.12)',  '#f87171', `P${p} High`]  :
    p >= 5  ? ['rgba(245,158,11,.12)', '#f59e0b', `P${p} Med`]   :
              ['rgba(100,116,139,.12)','#94a3b8', `P${p || 0}`];
  return (
    <span style={{ background: bg, color, boxShadow: `inset 0 0 0 1px ${color}44`, fontSize: 9, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase', padding: '2px 7px', borderRadius: 20 }}>
      {label}
    </span>
  );
};

// ─── ConfirmDialog ────────────────────────────────────────────────────────
const ConfirmDialog = ({ open, onConfirm, onCancel }) => (
  <AnimatePresence>
    {open && (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.7)', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 60 }}>
        <motion.div initial={{ scale: .92, y: 10 }} animate={{ scale: 1, y: 0 }} exit={{ scale: .92 }}
          style={{ background: '#0d1526', border: '1px solid rgba(255,255,255,.12)', borderRadius: 16, padding: 28, maxWidth: 360, width: '100%', textAlign: 'center' }}>
          <AlertTriangle size={28} color="#f87171" style={{ margin: '0 auto 12px' }} />
          <p style={{ fontSize: 13, fontWeight: 700, color: '#e2e8f0', marginBottom: 8 }}>Delete Announcement?</p>
          <p style={{ fontSize: 12, color: '#64748b', marginBottom: 22, lineHeight: 1.6 }}>This announcement will be permanently removed and will no longer appear on the site.</p>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
            <Btn variant="ghost" onClick={onCancel}>Cancel</Btn>
            <Btn variant="red" onClick={onConfirm}><Trash2 size={12} /> Delete</Btn>
          </div>
        </motion.div>
      </motion.div>
    )}
  </AnimatePresence>
);

// ─── Announcement Drawer ──────────────────────────────────────────────────
const EMPTY_FORM = {
  text: '', link: '', linkText: '', active: true,
  priority: 0, backgroundColor: '#1E88E5', textColor: '#FFFFFF',
};

const AnnouncementDrawer = ({ open, onClose, form, setForm, onSubmit, submitting, isEdit }) => {
  const set = key => e => setForm(p => ({ ...p, [key]: e.target.value }));

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', backdropFilter: 'blur(4px)', zIndex: 40 }}
            onClick={onClose} />
          <motion.div
            initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 320, damping: 38 }}
            style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '100%', maxWidth: 460, background: '#0d1526', borderLeft: '1px solid rgba(255,255,255,.1)', zIndex: 50, display: 'flex', flexDirection: 'column' }}
          >
            {/* Header */}
            <div style={{ padding: '16px 20px', borderBottom: '1px solid rgba(255,255,255,.06)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
              <div>
                <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.16em', color: 'rgba(59,130,246,.8)', marginBottom: 3 }}>{isEdit ? 'Editing' : 'New Announcement'}</p>
                <h3 style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{isEdit ? 'Edit Announcement' : 'Add Announcement'}</h3>
              </div>
              <button onClick={onClose} style={{ background: 'rgba(255,255,255,.06)', border: '1px solid rgba(255,255,255,.08)', borderRadius: 8, padding: '5px 8px', cursor: 'pointer', color: '#94a3b8', display: 'flex' }}><X size={14} /></button>
            </div>

            {/* Body */}
            <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

                {/* Text */}
                <div>
                  <FieldLabel>Announcement Text *</FieldLabel>
                  <textarea value={form.text} onChange={set('text')} placeholder="e.g. 🎉 Free shipping on orders above 50π this weekend!" rows={3}
                    style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 9, padding: '8px 11px', color: '#e2e8f0', fontSize: 12, width: '100%', outline: 'none', resize: 'vertical', fontFamily: 'inherit' }} />
                </div>

                {/* Link fields */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <FieldLabel>Link URL (optional)</FieldLabel>
                    <div style={{ position: 'relative' }}>
                      <Link size={11} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: '#475569', pointerEvents: 'none' }} />
                      <DI value={form.link} onChange={set('link')} placeholder="https://…" style={{ paddingLeft: 26 }} />
                    </div>
                  </div>
                  <div>
                    <FieldLabel>Link Text (optional)</FieldLabel>
                    <DI value={form.linkText} onChange={set('linkText')} placeholder="Learn more" />
                  </div>
                </div>

                {/* Priority */}
                <div>
                  <FieldLabel>Priority (higher = first)</FieldLabel>
                  <DI type="number" value={form.priority} onChange={set('priority')} placeholder="0" />
                  <p style={{ fontSize: 10, color: '#334155', marginTop: 4 }}>0 = default · 5+ = medium · 10+ = high priority</p>
                </div>

                {/* Colors */}
                <div style={{ borderTop: '1px solid rgba(255,255,255,.05)', paddingTop: 14 }}>
                  <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.14em', color: '#334155', marginBottom: 12 }}>Appearance</p>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <FieldLabel>Background Color</FieldLabel>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input type="color" value={form.backgroundColor} onChange={set('backgroundColor')}
                          style={{ width: 36, height: 36, borderRadius: 8, border: '1px solid rgba(255,255,255,.1)', background: 'none', cursor: 'pointer', padding: 2 }} />
                        <input value={form.backgroundColor} onChange={set('backgroundColor')}
                          style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 9, padding: '7px 10px', color: '#e2e8f0', fontSize: 11, fontFamily: 'monospace', flex: 1, outline: 'none' }} />
                      </div>
                    </div>
                    <div>
                      <FieldLabel>Text Color</FieldLabel>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input type="color" value={form.textColor} onChange={set('textColor')}
                          style={{ width: 36, height: 36, borderRadius: 8, border: '1px solid rgba(255,255,255,.1)', background: 'none', cursor: 'pointer', padding: 2 }} />
                        <input value={form.textColor} onChange={set('textColor')}
                          style={{ background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.1)', borderRadius: 9, padding: '7px 10px', color: '#e2e8f0', fontSize: 11, fontFamily: 'monospace', flex: 1, outline: 'none' }} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Live preview banner */}
                <div>
                  <FieldLabel>Live Preview</FieldLabel>
                  <div style={{ borderRadius: 10, overflow: 'hidden', border: '1px solid rgba(255,255,255,.08)' }}>
                    <div style={{ background: form.backgroundColor, color: form.textColor, padding: '10px 16px', textAlign: 'center', fontSize: 13, fontWeight: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, minHeight: 44, transition: 'background .2s, color .2s' }}>
                      {form.text || <span style={{ opacity: .4 }}>Your announcement text will appear here…</span>}
                      {form.link && form.linkText && (
                        <span style={{ textDecoration: 'underline', opacity: .85, fontSize: 11, fontWeight: 700 }}>{form.linkText} →</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Active toggle */}
                <label style={{ display: 'flex', alignItems: 'center', gap: 12, background: 'rgba(59,130,246,.06)', border: '1px solid rgba(59,130,246,.15)', borderRadius: 10, padding: '10px 14px', cursor: 'pointer' }}>
                  <Toggle value={form.active} onChange={() => setForm(p => ({ ...p, active: !p.active }))} accent="#3b82f6" />
                  <div>
                    <p style={{ fontSize: 12, fontWeight: 600, color: '#e2e8f0' }}>Active</p>
                    <p style={{ fontSize: 10, color: '#475569', marginTop: 2 }}>Display this announcement on the site</p>
                  </div>
                </label>
              </div>
            </div>

            {/* Footer */}
            <div style={{ padding: '14px 20px', borderTop: '1px solid rgba(255,255,255,.06)', display: 'flex', gap: 10, flexShrink: 0 }}>
              <Btn variant="ghost" onClick={onClose} style={{ flex: 1, justifyContent: 'center' }}>Cancel</Btn>
              <Btn variant="blue" onClick={onSubmit} disabled={submitting} style={{ flex: 2, justifyContent: 'center' }}>
                {submitting
                  ? <><div style={{ width: 12, height: 12, borderRadius: '50%', border: '2px solid rgba(250, 96, 181, 0.4)', borderTopColor: '#60a5fa', animation: 'spin .7s linear infinite' }} />{isEdit ? 'Saving…' : 'Publishing…'}</>
                  : isEdit ? <><Check size={13} /> Save Changes</> : <><Plus size={13} /> Publish Announcement</>
                }
              </Btn>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

// ─── Announcement Row card ────────────────────────────────────────────────
const AnnouncementRow = ({ ann, onEdit, onDelete, onToggleActive, index }) => (
  <motion.div
    initial={{ opacity: 0, x: -16 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ delay: index * .05, duration: .4, ease: [.22, 1, .36, 1] }}
    style={{
      background: 'rgba(17,24,39,.9)',
      border: `1px solid ${ann.active ? 'rgba(59,130,246,.2)' : 'rgba(255,255,255,.05)'}`,
      borderRadius: 14,
      overflow: 'hidden',
      boxShadow: ann.active ? '0 0 18px rgba(59,130,246,.07)' : 'none',
    }}
  >
    {/* Color strip = preview of banner bg */}
    <div style={{ height: 4, background: ann.backgroundColor || '#1E88E5', opacity: .8 }} />

    <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
      {/* Icon */}
      <div style={{ width: 38, height: 38, borderRadius: 10, background: ann.active ? 'rgba(59,130,246,.15)' : 'rgba(100,116,139,.1)', border: `1px solid ${ann.active ? 'rgba(59,130,246,.3)' : 'rgba(100,116,139,.2)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Bell size={16} color={ann.active ? '#60a5fa' : '#475569'} strokeWidth={1.8} />
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: 13, fontWeight: 600, color: ann.active ? '#e2e8f0' : '#475569', lineHeight: 1.4, marginBottom: 5 }}>{ann.text}</p>
        {ann.link && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 5 }}>
            <Link size={10} color="#475569" />
            <span style={{ fontSize: 10, color: '#334155', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 240 }}>{ann.link}</span>
            {ann.linkText && <span style={{ fontSize: 10, color: '#60a5fa', fontWeight: 600 }}>"{ann.linkText}"</span>}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <PriorityBadge priority={ann.priority} />
          <span style={{
            background: ann.active ? 'rgba(16,185,129,.12)' : 'rgba(100,116,139,.12)',
            color: ann.active ? '#34d399' : '#94a3b8',
            boxShadow: `inset 0 0 0 1px ${ann.active ? 'rgba(16,185,129,.3)' : 'rgba(100,116,139,.25)'}`,
            fontSize: 9, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase',
            padding: '2px 7px', borderRadius: 20,
          }}>
            {ann.active ? 'Active' : 'Inactive'}
          </span>
          {/* Color swatches */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <div title="Background" style={{ width: 12, height: 12, borderRadius: 3, background: ann.backgroundColor || '#1E88E5', border: '1px solid rgba(255,255,255,.15)' }} />
            <div title="Text" style={{ width: 12, height: 12, borderRadius: 3, background: ann.textColor || '#fff', border: '1px solid rgba(255,255,255,.15)' }} />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
        <Toggle value={ann.active} onChange={() => onToggleActive(ann)} accent="#3b82f6" />
        <Btn size="xs" variant="ghost" onClick={() => onEdit(ann)}><Edit2 size={10} /></Btn>
        <Btn size="xs" variant="red"   onClick={() => onDelete(ann.id)}><Trash2 size={10} /></Btn>
      </div>
    </div>
  </motion.div>
);

// ═══════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════
export default function AnnouncementManager() {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading]             = useState(true);
  const [submitting, setSubmitting]       = useState(false);
  const [globalEnabled, setGlobalEnabled] = useState(true);
  const [drawerOpen, setDrawerOpen]       = useState(false);
  const [form, setForm]                   = useState(EMPTY_FORM);
  const [editingId, setEditingId]         = useState(null);
  const [confirmId, setConfirmId]         = useState(null);
  const [filterStatus, setFilterStatus]   = useState('all');

  // ── fetch ──────────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'announcements'), orderBy('priority', 'desc'));
      const snap = await getDocs(q);
      setAnnouncements(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      const settingsSnap = await getDocs(collection(db, 'settings'));
      const settings = settingsSnap.docs.find(d => d.id === 'announcementSettings');
      if (settings) setGlobalEnabled(settings.data().enabled ?? true);
    } catch { toast.error('Failed to load announcements'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── global toggle ──────────────────────────────────────────────────
  const toggleGlobal = async () => {
    try {
      await setDoc(doc(db, 'settings', 'announcementSettings'), { enabled: !globalEnabled }, { merge: true });
      setGlobalEnabled(v => !v);
      toast.success(`Announcements ${!globalEnabled ? 'enabled' : 'disabled'}`);
    } catch { toast.error('Failed to update setting'); }
  };

  // ── toggle active ──────────────────────────────────────────────────
  const handleToggleActive = async (ann) => {
    try {
      await setDoc(doc(db, 'announcements', ann.id), { active: !ann.active }, { merge: true });
      setAnnouncements(p => p.map(a => a.id === ann.id ? { ...a, active: !ann.active } : a));
      toast.success(`Announcement ${!ann.active ? 'activated' : 'deactivated'}`);
    } catch { toast.error('Failed to update'); }
  };

  // ── open drawer ────────────────────────────────────────────────────
  const openAdd  = () => { setForm(EMPTY_FORM); setEditingId(null); setDrawerOpen(true); };
  const openEdit = a  => { setForm({ ...EMPTY_FORM, ...a }); setEditingId(a.id); setDrawerOpen(true); };

  // ── submit ─────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!form.text.trim()) { toast.error('Announcement text is required'); return; }
    setSubmitting(true);
    try {
      const data = { ...form, priority: Number(form.priority) || 0 };
      if (editingId) {
        await setDoc(doc(db, 'announcements', editingId), data, { merge: true });
        toast.success('Announcement updated!');
      } else {
        await setDoc(doc(collection(db, 'announcements')), { ...data, createdAt: new Date() });
        toast.success('Announcement published!');
      }
      setDrawerOpen(false); setEditingId(null); fetchData();
    } catch { toast.error(editingId ? 'Failed to update' : 'Failed to publish'); }
    finally { setSubmitting(false); }
  };

  // ── delete ─────────────────────────────────────────────────────────
  const handleDeleteConfirm = async () => {
    try {
      await deleteDoc(doc(db, 'announcements', confirmId));
      setAnnouncements(p => p.filter(a => a.id !== confirmId));
      toast.success('Announcement deleted!');
    } catch { toast.error('Failed to delete'); }
    finally { setConfirmId(null); }
  };

  // ── stats + filter ─────────────────────────────────────────────────
  const stats = useMemo(() => ({
    total:    announcements.length,
    active:   announcements.filter(a => a.active).length,
    inactive: announcements.filter(a => !a.active).length,
  }), [announcements]);

  const filtered = useMemo(() => {
    if (filterStatus === 'active')   return announcements.filter(a => a.active);
    if (filterStatus === 'inactive') return announcements.filter(a => !a.active);
    return announcements;
  }, [announcements, filterStatus]);

  // ─── LOADING ────────────────────────────────────────────────────────
  if (loading && announcements.length === 0) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 400, gap: 16 }}>
      <div style={{ width: 44, height: 44, borderRadius: '50%', border: '3px solid rgba(255,255,255,.3)', borderTopColor: 'white', animation: 'spin .8s linear infinite' }} />
      <p style={{ color: '#64748b', fontSize: 13, fontWeight: 600 }}>Loading announcements…</p>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  // ─── RENDER ─────────────────────────────────────────────────────────
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ paddingBottom: 48 }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}} input::placeholder,textarea::placeholder{color:#334155}`}</style>

      {/* ── Hero Banner ── */}
      <motion.div initial={{ opacity: 0, y: -14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .5, ease: [.22, 1, .36, 1] }}
        style={{ position: 'relative', overflow: 'hidden', borderRadius: 18, background: 'linear-gradient(120deg,#0f172a 0%,#1a1f35 50%,#0f172a 100%)', border: '1px solid rgba(255,255,255,.07)', padding: '20px 24px', marginBottom: 20 }}>
        <div style={{ position: 'absolute', top: -40, right: -40, width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle,rgba(59,130,246,.12),transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: -30, left: '20%', width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle,rgba(99,102,241,.08),transparent 70%)', pointerEvents: 'none' }} />
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <p style={{ fontSize: 9, textTransform: 'uppercase', letterSpacing: '.2em', color: 'rgba(59,130,246,.8)', marginBottom: 5 }}>Broadcast</p>
            <h1 style={{ fontSize: 20, fontWeight: 800, color: '#fff', display: 'flex', alignItems: 'center', gap: 10, marginBottom: 3 }}>
              <Megaphone size={20} color="#3b82f6" strokeWidth={2} /> Announcement Manager
            </h1>
            <p style={{ fontSize: 11, color: '#475569' }}>Manage site-wide announcements and broadcast banners</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            {/* Global display toggle */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(255,255,255,.04)', border: '1px solid rgba(255,255,255,.08)', borderRadius: 10, padding: '8px 14px' }}>
              <Eye size={13} color={globalEnabled ? '#60a5fa' : '#475569'} />
              <span style={{ fontSize: 11, fontWeight: 600, color: globalEnabled ? '#e2e8f0' : '#475569' }}>Display</span>
              <Toggle value={globalEnabled} onChange={toggleGlobal} accent="#3b82f6" />
            </div>
            <Btn variant="ghost" onClick={fetchData} disabled={loading}>
              <RefreshCw size={13} style={{ animation: loading ? 'spin .8s linear infinite' : 'none' }} />
            </Btn>
            <Btn variant="blue" onClick={openAdd}>
              <Plus size={13} /> Add Announcement
            </Btn>
          </div>
        </div>
      </motion.div>

      {/* ── Stats ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))', gap: 12, marginBottom: 20 }}>
        <MiniStat label="Total"         value={stats.total}    accent="#3b82f6" icon={Megaphone} index={0} />
        <MiniStat label="Active"        value={stats.active}   accent="#34d399" icon={Bell}      index={1} />
        <MiniStat label="Inactive"      value={stats.inactive} accent="#94a3b8" icon={Bell}      index={2} />
        <MiniStat label="Display All"   value={globalEnabled ? 'ON' : 'OFF'} accent={globalEnabled ? '#3b82f6' : '#475569'} icon={Eye} index={3} />
      </div>

      {/* ── Filter tabs ── */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        {[['all', 'All'], ['active', 'Active'], ['inactive', 'Inactive']].map(([val, label]) => (
          <button key={val} onClick={() => setFilterStatus(val)} style={{
            padding: '5px 14px', borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: 'pointer',
            background: filterStatus === val ? 'rgba(59,130,246,.15)' : 'rgba(255,255,255,.05)',
            color: filterStatus === val ? '#60a5fa' : '#475569',
            border: filterStatus === val ? '1px solid rgba(59,130,246,.3)' : '1px solid rgba(255,255,255,.07)',
            transition: 'all .15s',
          }}>{label}</button>
        ))}
        <p style={{ fontSize: 10, color: '#334155', marginLeft: 'auto', alignSelf: 'center' }}>
          <span style={{ color: '#3b82f6', fontWeight: 700 }}>{filtered.length}</span> / {announcements.length}
        </p>
      </div>

      {/* ── List ── */}
      {filtered.length === 0 ? (
        <GlassPanel style={{ padding: '48px 24px', textAlign: 'center' }}>
          <Megaphone size={32} style={{ margin: '0 auto 12px', opacity: .2, display: 'block', color: '#475569' }} />
          <p style={{ fontSize: 13, fontWeight: 600, color: '#334155' }}>
            {filterStatus !== 'all' ? `No ${filterStatus} announcements` : 'No announcements yet'}
          </p>
          {filterStatus === 'all' && (
            <div style={{ marginTop: 14 }}>
              <Btn variant="blue" onClick={openAdd}><Plus size={12} /> Create your first announcement</Btn>
            </div>
          )}
        </GlassPanel>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map((ann, idx) => (
            <AnnouncementRow
              key={ann.id}
              ann={ann}
              onEdit={openEdit}
              onDelete={id => setConfirmId(id)}
              onToggleActive={handleToggleActive}
              index={idx}
            />
          ))}
        </div>
      )}

      {/* ── Drawer + Confirm ── */}
      <AnnouncementDrawer
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setEditingId(null); }}
        form={form}
        setForm={setForm}
        onSubmit={handleSubmit}
        submitting={submitting}
        isEdit={!!editingId}
      />

      <ConfirmDialog
        open={!!confirmId}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmId(null)}
      />
    </motion.div>
  );
}